import { type MouseEvent } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { CircularProgress, Stack } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';

export type CuePlayMode = 'jump' | 'insert';

type PlayCueConfirmationModalProps = {
  open: boolean;
  mediaTitle: string;
  onCancel: () => void;
  isLoading?: boolean;
  /**
   * When true (flow-control auto-cue), show Jump vs Play-once.
   * When false, a single Confirm runs onConfirm (e.g. PERFORM device play).
   */
  showCuePlayModes?: boolean;
  /** When `showCuePlayModes` is true, hide Play once (section jump has no insert). Default true. */
  showPlayInsert?: boolean;
  onJumpCue?: () => void;
  onPlayInsert?: () => void;
  onConfirm?: () => void;
};

/** Confirm before playing media — optional jump vs insert choice for auto-cue. */
export function PlayCueConfirmationModal({
  open,
  mediaTitle,
  onCancel,
  isLoading,
  showCuePlayModes = false,
  showPlayInsert = true,
  onJumpCue,
  onPlayInsert,
  onConfirm,
}: PlayCueConfirmationModalProps) {
  const t = useTranslations();

  const handleCancel = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onCancel();
  };

  return (
    <Dialog
      onClick={(e) => e.stopPropagation()}
      open={open}
      onClose={onCancel}
      aria-labelledby="play-cue-confirmation-title"
      aria-describedby="play-cue-confirmation-text"
    >
      <DialogTitle id="play-cue-confirmation-title">
        {t('media.playConfirmationTitle' as TranslationKey, { mediaTitle })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="play-cue-confirmation-text" component="div">
          {showCuePlayModes && showPlayInsert ? (
            <Stack spacing={1.5}>
              <span>{t('media.playConfirmationBody' as TranslationKey)}</span>
              <span>{t('media.playConfirmationJumpHint' as TranslationKey)}</span>
              <span>{t('media.playConfirmationInsertHint' as TranslationKey)}</span>
            </Stack>
          ) : showCuePlayModes ? (
            t('media.playConfirmationJumpOnlyBody' as TranslationKey)
          ) : (
            t('media.playConfirmationBody' as TranslationKey)
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, pb: 2 }}>
        <Button color="primary" onClick={handleCancel} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        {showCuePlayModes ? (
          <>
            {showPlayInsert ? (
              <Button
                variant="outlined"
                disabled={isLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  onPlayInsert?.();
                }}
              >
                {isLoading ? <CircularProgress size={20} /> : t('media.playInsert' as TranslationKey)}
              </Button>
            ) : null}
            <Button
              variant="contained"
              disabled={isLoading}
              autoFocus
              onClick={(e) => {
                e.stopPropagation();
                onJumpCue?.();
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : t('media.playJumpCue' as TranslationKey)}
            </Button>
          </>
        ) : (
          <Button
            loading={isLoading}
            variant="contained"
            autoFocus
            onClick={(e) => {
              e.stopPropagation();
              onConfirm?.();
            }}
            sx={{ m: '0.4rem' }}
          >
            {isLoading ? <CircularProgress size={24} /> : t('media.confirmPlay' as TranslationKey)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
